import { Component, type ErrorInfo, type ReactNode } from "react"
import { captureException } from "@sentry/nextjs"

import Box from "@mui/material/Box"
import AppHeader from "@/organism/app_header"
import { getAwsRum } from "@/utils/aws_rum"
import ErrorBoundaryText from "../molecules/error_boundary_text"

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    minHeight: "calc(100vh - 75px)",
    backgroundColor: "#f5f5f5",
  },
} as const

type ErrorBoundaryProps = {
  children?: ReactNode
  showErrorMessage?: boolean
  notifySentry?: boolean
}

type ErrorBoundaryState = {
  hasError: boolean
  error: Error | null
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  static readonly defaultProps = {
    children: null,
    showErrorMessage: true,
    notifySentry: true,
  }

  state: ErrorBoundaryState = { hasError: false, error: null }

  componentDidCatch(err: Error, _info: ErrorInfo): void {
    this.setState({ hasError: true, error: err })
    if (!this.props.notifySentry) {
      console.error(err)
      captureException(err)
    }

    const awsRum = getAwsRum()
    if (awsRum) {
      awsRum.recordError(err)
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.showErrorMessage) {
        return (
          <Box sx={styles.container}>
            <AppHeader />
            <ErrorBoundaryText error={this.state.error} />
          </Box>
        )
      }
      return <></>
    }
    if (this.props.children) {
      return this.props.children
    }
    return <></>
  }
}

export default ErrorBoundary
